import { flatten, isArray, isEmpty, mergeWith, remove } from "lodash";
import { setCurrentWorkspaceId } from "src/store/app";
import QueryBuilder from "src/ui/shared/form/QueryBuilderField/QueryBuilderField.utils";
import { PartialDeep } from "type-fest";

import {
    apiUpdateGroupMembers,
    createDevice as apiCreateDevice,
    createGroup as apiCreateGroup,
    deleteGroup as apiDeleteGroup,
    fetchDevice as apiFetchDevice,
    fetchGroups as apiFetchGroups,
    fetchLicenses as apiFetchLicenses,
    generateLicenses as apiGenerateLicenses,
    revokeLicense as apiRevokeLicense,
    updateDevice as apiUpdateDevice,
    updateGroup as apiUpdateGroup,
    updateGroupParent as apiUpdateGroupParent,
    UpdateGroupPayload,
    updateLicense as apiUpdateLicense,
    updatePolicy as apiUpdatePolicy,
} from "@dashboard/devices/api";
import { devicesAdapter, groupsAdapter, licensesAdapter, normalizeLicenses } from "@dashboard/devices/store/helpers";
import { Device, Group, License } from "@dashboard/devices/types";
import {
    deleteUniversalLicenseGroup,
    updateUniversalLicenseGroup as apiUpdateUniversalLicenseGroup,
} from "@dashboard/provisioning/api";
import { normalizeGroups } from "@groups/utils";
import { createAsyncThunk, createSlice, EntityState, PayloadAction } from "@reduxjs/toolkit";

import { getDevicesStoreFromUrl } from "./helpers/device";
import { normalizeDevice } from "./helpers/entities";
import { BatchActionState, DetailsState } from "./selectors";

export * from "./selectors";

export const fetchGroups = createAsyncThunk("groups/fetch", apiFetchGroups);
export const createGroup = createAsyncThunk("groups/create", apiCreateGroup);
export const deleteGroup = createAsyncThunk("groups/delete", apiDeleteGroup);
export const updateGroupParent = createAsyncThunk("groups/updateParent", apiUpdateGroupParent);
export const updateGroupMembers = createAsyncThunk("groups/updateMembers", apiUpdateGroupMembers);
export const updateGroup = createAsyncThunk<API.Group[], UpdateGroupPayload>("groups/update", apiUpdateGroup);

export const updatePolicy = createAsyncThunk<API.Group[] | undefined, { groupId: number; policy: string }>(
    "policy/update",
    apiUpdatePolicy,
);

export const fetchLicenses = createAsyncThunk("licenses/fetch", apiFetchLicenses);
export const updateLicense = createAsyncThunk("licenses/update", apiUpdateLicense);
export const revokeLicense = createAsyncThunk("licenses/revoke", apiRevokeLicense);
export const generateLicenses = createAsyncThunk("licenses/generate", apiGenerateLicenses);

export const updateUniversalLicense = createAsyncThunk("provisioning/updateUniversalLicense", apiUpdateUniversalLicenseGroup);
export const deleteUniversalLicense = createAsyncThunk("provisioning/DeleteUniversalLicense", deleteUniversalLicenseGroup);

export const fetchDevice = createAsyncThunk("devices/fetch", apiFetchDevice);
export const createDevice = createAsyncThunk("devices/create", apiCreateDevice);
export const updateDevice = createAsyncThunk("devices/update", apiUpdateDevice);

const urlDevicesStore = getDevicesStoreFromUrl();

export type ListStatus = "pending" | "fulfilled" | "rejected" | "updating";
type LocalState = {
    list: {
        status: ListStatus | null;

        groups: {
            status: ListStatus | null;
            items: EntityState<Group, number>;
        };
        licenses: {
            status: ListStatus | null;
            statusByGroupId: Record<number, ListStatus>;
            batchFetch: boolean;
            items: EntityState<License, number>;
        };
        devices: {
            items: EntityState<Device, number>;
        };
    };
    details: DetailsState;
    batchAction: BatchActionState;
    pane: {
        tab: string;
        status: ListStatus | null;
        editEnabled: boolean;
    };
    search: {
        query: string;
    };
    devicePageExternalFilterUpdate: number;
};

const toStoreGroup = (group: API.Group): Group => ({
    ...group,
    members: group.members ?? [],
});

const replaceReturnedGroups = (state: LocalState, groups: API.Group[]) => {
    groupsAdapter.upsertMany(
        state.list.groups.items,
        groups.map(toStoreGroup),
    );
};

const syncLicenseGroupMembership = (state: LocalState, groups: API.Group[]) => {
    licensesAdapter.updateMany(
        state.list.licenses.items,
        flatten(
            groups.map((group) =>
                (group.licenseIds ?? []).map((licenseId) => ({
                    id: licenseId,
                    changes: {
                        groupId: group.id,
                        groupName: group.name,
                    },
                })),
            ),
        ),
    );
};

const initialState: LocalState = {
    list: {
        status: null,

        groups: { status: null, items: groupsAdapter.getInitialState() },
        licenses: {
            status: null,
            statusByGroupId: {},
            batchFetch: true,
            items: licensesAdapter.getInitialState(),
        },
        devices: {
            items: devicesAdapter.getInitialState(),
        },
    },
    details: null,
    search: {
        query: "",
    },
    pane: {
        tab: "SUMMARY",
        editEnabled: false,
        status: null,
    },
    batchAction: {
        isActive: false,
        // action type
        type: null,
        // entity type for a batch action
        entity: null,
        // entity-ids selected for a batch action
        selection: [],
        targetId: null,
    },
    devicePageExternalFilterUpdate: 0,
};

const initialWithLastPersistedState: LocalState = mergeWith(
    initialState,
    urlDevicesStore,
    {
        // override anything from "initial-state" & "last-persisted-state" here
    },
    (obj: PartialDeep<LocalState>, src: PartialDeep<LocalState>) => {
        if (isArray(obj)) {
            return src;
        }
    },
);

const resetDetails = (state: LocalState) => {
    state.details = initialState.details;
};
const resetBatchAction = (state: LocalState) => {
    state.batchAction = initialState.batchAction;
};
const clearSidePanel = (state: LocalState) => {
    state.details = null;
    state.pane.editEnabled = false;
};

const subscriptionsSlice = createSlice({
    name: "subscription",
    initialState: initialWithLastPersistedState,
    reducers: {
        showDetails(state, { payload }: { payload: NonNullable<DetailsState> }) {
            state.details = payload;
        },
        closeDetails(state) {
            clearSidePanel(state);
        },
        clearAllFilters(state) {
            state.search.query = "";
        },
        updateSearchQuery(state, { payload }: { payload: string }) {
            state.search.query = payload ?? "";
        },
        setBatchMode(
            state,
            {
                payload,
            }: {
                payload: {
                    type: BatchActionState["type"];
                    entity: BatchActionState["entity"];
                };
            },
        ) {
            const { type, entity } = payload;
            // ignore if same mode active
            if (entity === state.batchAction.entity) {
                return;
            }

            state.batchAction = {
                ...state.batchAction,
                isActive: true,
                type,
                entity,
                selection: [],
                targetId: null,
            };
            resetDetails(state);
        },
        closeBatchMode(state) {
            resetBatchAction(state);
        },
        addBatchSelection(state, { payload }: { payload: number }) {
            const entityId = payload;
            state.batchAction.selection.push(entityId);
        },
        removeBatchSelection(state, { payload }: { payload: number }) {
            const entityId = payload;
            remove(state.batchAction.selection, (selectionId) => selectionId === entityId);
        },
        addBatchSelectionBulk(state, { payload }: { payload: number[] }) {
            const existingSet = new Set(state.batchAction.selection);
            const newIds = payload.filter((id) => !existingSet.has(id));
            state.batchAction.selection.push(...newIds);
        },
        removeBatchSelectionBulk(state, { payload }: { payload: number[] }) {
            const toRemoveSet = new Set(payload);
            state.batchAction.selection = state.batchAction.selection.filter((id) => !toRemoveSet.has(id));
        },
        setBatchTargetId(state, { payload }: { payload: BatchActionState["targetId"] }) {
            state.batchAction.targetId = payload;
        },
        setPaneTab(state, { payload }: { payload: string }) {
            state.pane.tab = payload;
        },
        applyDevicePageFiltersExternally(state) {
            state.devicePageExternalFilterUpdate = Date.now();
        },
        setPaneEditEnabled(state, { payload }: { payload: boolean }) {
            state.pane.editEnabled = payload;
        },
        // Builds a flat queryMap on each license for use by QueryBuilderField.
        // Pass a list of IDs to rebuild only specific licenses; omit to rebuild all.
        createLicensesMap(state, { payload: ids }: PayloadAction<number[] | undefined>) {
            const allLicenses = licensesAdapter.getSelectors().selectAll(state.list.licenses.items);
            const idsToUpdate = ids ? new Set(ids) : null;
            const licensesToUpdate = idsToUpdate ? allLicenses.filter((license) => idsToUpdate.has(license.id)) : allLicenses;

            const updatedLicenses = licensesToUpdate.map((license) => {
                // Exclude the existing queryMap so it doesn't pollute the result
                // with queryMap.* keys on subsequent invocations.
                const { queryMap: _ignored, ...licenseData } = license;
                const queryMap = QueryBuilder.generateQueryMap(licenseData);
                return { ...license, queryMap };
            });

            licensesAdapter.upsertMany(state.list.licenses.items, updatedLicenses);
        },
    },
    extraReducers: (builder) => {
        builder

            .addCase(setCurrentWorkspaceId, (state) => {
                groupsAdapter.removeAll(state.list.groups.items);
            })
            .addCase(fetchGroups.pending, (state) => {
                state.list.groups.status = "pending";
            })
            .addCase(fetchGroups.rejected, (state) => {
                state.list.groups.status = "rejected";
            })
            .addCase(fetchGroups.fulfilled, (state, { payload }) => {
                state.list.groups.status = "fulfilled";
                const groups = normalizeGroups(payload);
                groupsAdapter.setAll(state.list.groups.items, groups);
            })
            .addCase(createGroup.fulfilled, (state, { payload }) => {
                resetDetails(state);

                if (!payload) {
                    return;
                }

                groupsAdapter.addOne(state.list.groups.items, payload);
            })
            .addCase(updateGroup.fulfilled, (state, { payload, meta }) => {
                if (!payload?.length) {
                    return;
                }

                if (meta.arg.includeStatistics || meta.arg.includeParents) {
                    replaceReturnedGroups(state, payload);
                    syncLicenseGroupMembership(state, payload);
                    resetBatchAction(state);
                    return;
                }

                const group = payload[0];
                const changes: Partial<Group> = {};
                if (meta.arg.name !== undefined) {
                    changes.name = group.name;
                }
                if (meta.arg.description !== undefined) {
                    changes.description = group.description;
                }
                if (Object.keys(changes).length === 0) {
                    return;
                }

                groupsAdapter.updateOne(state.list.groups.items, { id: group.id, changes });

                if (changes.name) {
                    const cached = groupsAdapter.getSelectors().selectById(state.list.groups.items, group.id);
                    if (cached) {
                        licensesAdapter.updateMany(
                            state.list.licenses.items,
                            cached.licenseIds.map((licenseId) => ({
                                id: licenseId,
                                changes: { groupName: group.name },
                            })),
                        );
                    }
                }
            })

            .addCase(updateGroupMembers.pending, (state) => {
                state.pane.status = "pending";
            })
            .addCase(updateGroupMembers.rejected, (state) => {
                state.pane.status = "rejected";
            })
            .addCase(updateGroupMembers.fulfilled, (state, { payload, meta }) => {
                state.pane.status = "fulfilled";

                if (isEmpty(payload)) {
                    groupsAdapter.removeOne(state.list.groups.items, meta.arg.id);
                } else {
                    const groupsUpdate = payload.map((g) => ({ id: g.id, changes: { members: g.members } }));
                    groupsAdapter.updateMany(state.list.groups.items, groupsUpdate);
                    resetBatchAction(state);
                }
            })
            .addCase(updateGroupParent.pending, (state) => {
                state.pane.status = "pending";
            })
            .addCase(updateGroupParent.rejected, (state) => {
                state.pane.status = "rejected";
            })
            .addCase(updateGroupParent.fulfilled, (state, { payload }) => {
                state.pane.status = "fulfilled";
                if (!payload?.length) {
                    return;
                }
                replaceReturnedGroups(state, payload);
            })
            .addCase(updatePolicy.fulfilled, (state, { payload }) => {
                if (!payload?.length) {
                    return;
                }
                replaceReturnedGroups(state, payload);
            })
            .addCase(deleteGroup.fulfilled, (state, { meta }) => {
                if (!meta || !meta.arg) {
                    return;
                }
                const { id: groupId } = meta.arg;
                if (!groupId) {
                    return;
                }

                const group = groupsAdapter.getSelectors().selectById(state.list.groups.items, groupId);
                if (!group) {
                    return;
                }

                const isGroupPanelOpen = !state.batchAction.type && state.details?.type === "group";
                if (isGroupPanelOpen) {
                    clearSidePanel(state);
                }

                groupsAdapter.removeOne(state.list.groups.items, groupId);
            })
            .addCase(fetchLicenses.pending, (state, { meta }) => {
                if (meta.arg.isPrefetch || state.list.licenses.status === null) {
                    state.list.licenses.status = "pending";
                }
            })
            .addCase(fetchLicenses.fulfilled, (state, { payload, meta }) => {
                state.list.licenses.status = meta.arg.isPrefetch ? "updating" : "fulfilled";

                let newLicenses = {};
                let newDevices = {};

                const groups = groupsAdapter.getSelectors().selectAll(state.list.groups.items);

                payload.forEach((item) => {
                    const { licenses, devices } = normalizeLicenses(item.licenses.list, groups);
                    newLicenses = { ...newLicenses, ...licenses };
                    newDevices = { ...newDevices, ...devices };
                });

                licensesAdapter.setAll(state.list.licenses.items, newLicenses);
                devicesAdapter.setAll(state.list.devices.items, newDevices);
            })
            .addCase(fetchLicenses.rejected, (state) => {
                state.list.status = "rejected";
            })
            .addCase(updateLicense.fulfilled, (state, { meta }) => {
                const { id, comment } = meta.arg;
                const license = licensesAdapter.getSelectors().selectById(state.list.licenses.items, id);

                if (!license) {
                    return;
                }

                licensesAdapter.updateOne(state.list.licenses.items, {
                    id,
                    changes: {
                        comment: comment,
                    },
                });

                resetBatchAction(state);
            })
            .addCase(generateLicenses.fulfilled, (state, { payload, meta }) => {
                const groups = groupsAdapter.getSelectors().selectAll(state.list.groups.items);
                const { licenses } = normalizeLicenses(payload, groups);
                const group = groupsAdapter.getSelectors().selectById(state.list.groups.items, meta.arg.groupId);
                const replacedLicenesIds = payload.map((license) => license.id);
                if (group) {
                    groupsAdapter.updateOne(state.list.groups.items, {
                        id: meta.arg.groupId,
                        changes: {
                            licenseIds: [...group.licenseIds, ...replacedLicenesIds],
                        },
                    });
                }
                licensesAdapter.addMany(state.list.licenses.items, licenses);
            })
            .addCase(revokeLicense.fulfilled, (state, { payload }) => {
                licensesAdapter.removeOne(state.list.licenses.items, payload.id);
            })
            .addCase(updateUniversalLicense.fulfilled, (state, { payload }) => {
                const { model: license } = payload;

                licensesAdapter.updateOne(state.list.licenses.items, {
                    id: license.id,
                    changes: {
                        universalGroupName: license.universalGroupName,
                        universalGroupId: license.universalGroupId,
                    },
                });
            })
            .addCase(deleteUniversalLicense.fulfilled, (state, { payload }) => {
                const { model: license } = payload;

                licensesAdapter.updateOne(state.list.licenses.items, {
                    id: license.id,
                    changes: {
                        universalGroupName: null,
                        universalGroupId: null,
                    },
                });
            })
            .addCase(createDevice.fulfilled, (state, { payload, meta }) => {
                const group = groupsAdapter.getSelectors().selectById(state.list.groups.items, meta.arg.groupId);
                const license = payload.model.license;

                if (!license || !group) {
                    return;
                }

                const { licenses, devices } = normalizeLicenses([license], [group]);
                const normalizedLicense = licenses[license.id];
                if (normalizedLicense) {
                    licensesAdapter.upsertOne(state.list.licenses.items, normalizedLicense);
                }

                const device = license.device;
                if (device) {
                    const normalizedDevice = devices[device.id];
                    if (normalizedDevice) {
                        devicesAdapter.upsertOne(state.list.devices.items, normalizedDevice);
                    }
                }

                groupsAdapter.updateOne(state.list.groups.items, {
                    id: meta.arg.groupId,
                    changes: {
                        licenseIds: [...(group.licenseIds ?? []), license.id],
                    },
                });
            })
            .addCase(updateDevice.fulfilled, (state, { meta }) => {
                if (!meta || !meta.arg) {
                    return;
                }

                const { id, name, comment, description } = meta.arg;
                const device = devicesAdapter.getSelectors().selectById(state.list.devices.items, id);

                if (!device) {
                    return;
                }

                licensesAdapter.updateOne(state.list.licenses.items, {
                    id: device.licenseId,
                    changes: {
                        comment: comment,
                        device: { ...device, name, description },
                    },
                });

                resetBatchAction(state);
            })
            .addCase(fetchDevice.fulfilled, (state, { payload }) => {
                const license = licensesAdapter.getSelectors().selectById(state.list.licenses.items, payload.licenseId);
                const groups = groupsAdapter.getSelectors().selectById(state.list.groups.items, license.groupId);
                const device = normalizeDevice(payload, [groups]);

                if (license && device) {
                    licensesAdapter.updateOne(state.list.licenses.items, { id: license.id, changes: { device } });
                    devicesAdapter.upsertOne(state.list.devices.items, device);
                }
            });
    },
});

export default subscriptionsSlice.reducer;

export const {
    showDetails,
    closeDetails,
    clearAllFilters,
    updateSearchQuery,
    setBatchMode,
    addBatchSelection,
    removeBatchSelection,
    addBatchSelectionBulk,
    removeBatchSelectionBulk,
    closeBatchMode,
    setBatchTargetId,
    setPaneTab,
    setPaneEditEnabled,
    applyDevicePageFiltersExternally,
    createLicensesMap,
} = subscriptionsSlice.actions;
